import * as utils from "./utils.js";
import { log } from "./debug-utils.js";

/**
 * Track user engagement (time on page and scroll depth)
 * @param {Object} params - URL parameters
 * @param {Object} config - Configuration object
 * @returns {Object} - Controller for the tracking
 */
export function trackEngagement(params, config) {
  let timeElapsed = 0;
  let maxScrollPercent = 0;
  let timeThresholdMet = false;
  let scrollThresholdMet = false;
  let eventTriggered = false;
  let timeInterval = null;
  let scrollHandler = null;
  let unloadHandlers = [];
  let pageStartTime = Date.now();

  // Controller object for external control
  const controller = {
    start: startTracking,
    stop: stopTracking,
    isTracking: false,
    getStats: getTrackingStats,
    destroy: function () {
      this.stop();
      removeUnloadHandlers();
      timeElapsed = 0;
      maxScrollPercent = 0;
      timeThresholdMet = false;
      scrollThresholdMet = false;
      eventTriggered = false;
    },
  };

  // Get current tracking statistics
  function getTrackingStats() {
    return {
      timeElapsed,
      maxScrollPercent: parseFloat(maxScrollPercent.toFixed(2)),
      timeThresholdMet,
      scrollThresholdMet,
      eventTriggered,
      isTracking: controller.isTracking,
      timeThreshold: config.minTimeOnPageSeconds,
      scrollThreshold: config.minScrollDepthPercent,
    };
  }

  function startTracking() {
    if (controller.isTracking) return;
    controller.isTracking = true;
    setupUnloadTracking();

    log("Start track", "info", {
      timeThreshold: config.minTimeOnPageSeconds,
      scrollThreshold: config.minScrollDepthPercent,
    });

    // Time on page tracking
    timeInterval = setInterval(() => {
      timeElapsed++;

      // Debug log every 5 seconds
      if (timeElapsed % 5 === 0) {
        log(
          `Time on page: ${timeElapsed}s / ${config.minTimeOnPageSeconds}s`,
          "info",
          {
            timeElapsed,
            timeThreshold: config.minTimeOnPageSeconds,
            timeThresholdMet: timeElapsed >= config.minTimeOnPageSeconds,
          }
        );
      }

      // Check if time threshold is met
      if (!timeThresholdMet && timeElapsed >= config.minTimeOnPageSeconds) {
        timeThresholdMet = true;
        log(`Time threshold met (${timeElapsed}s)`, "info", { timeElapsed });
        checkThresholds();
      }

      const timerElement = document.getElementById(
        "vtd-session-total-time-value"
      );
      if (timerElement) {
        timerElement.textContent = utils.formatTime(timeElapsed);
      }
    }, 1000);

    // Scroll depth tracking
    scrollHandler = () => {
      // Calculate scroll depth as a percentage
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      const scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;

      // Update maximum scroll percentage
      const previousMax = maxScrollPercent;
      maxScrollPercent = Math.max(maxScrollPercent, scrollPercent);

      const scrollElement = document.getElementById(
        "vtd-session-total-scroll-value"
      );
      if (scrollElement) {
        scrollElement.textContent = `${maxScrollPercent.toFixed(2)}%`;
      }

      // Only log if scroll depth has increased significantly
      if (maxScrollPercent >= previousMax + 5) {
        log(
          `Scroll depth: ${maxScrollPercent.toFixed(2)}% / ${config.minScrollDepthPercent}%`,
          "info",
          {
            maxScrollPercent: maxScrollPercent.toFixed(2),
            scrollThreshold: config.minScrollDepthPercent,
            scrollThresholdMet:
              maxScrollPercent >= config.minScrollDepthPercent,
          }
        );
      }

      // Check if scroll threshold is met
      if (
        !scrollThresholdMet &&
        maxScrollPercent >= config.minScrollDepthPercent
      ) {
        scrollThresholdMet = true;
        log(`Scroll threshold met (${maxScrollPercent.toFixed(2)}%)`, "info", {
          maxScrollPercent: maxScrollPercent.toFixed(2),
        });
        checkThresholds();
      }
    };

    window.addEventListener("scroll", scrollHandler, { passive: true });
  }

  function stopTracking() {
    if (!controller.isTracking) return;

    controller.isTracking = false;

    log("Stopping engagement tracking", "info", getTrackingStats());

    if (timeInterval) {
      clearInterval(timeInterval);
      timeInterval = null;
    }

    if (scrollHandler) {
      window.removeEventListener("scroll", scrollHandler);
      scrollHandler = null;
    }
  }

  // Check if both thresholds are met
  function checkThresholds() {
    // Trigger if both conditions are met (or use OR logic if preferred)
    const thresholdsCombiningLogic = config.thresholdsCombiningLogic
      ? timeThresholdMet && scrollThresholdMet
      : timeThresholdMet || scrollThresholdMet;
    if (!eventTriggered && thresholdsCombiningLogic) {
      eventTriggered = true;
      // stopTracking();

      log("All thresholds met, preparing event", "info");

      // Prepare event data
      const eventData = {
        event: config.eventName,
        url: window.location.href,
        params: params,
        engagement: {
          timeOnPage: timeElapsed,
          scrollDepth: maxScrollPercent.toFixed(2),
        },
        user_data: {
          fbc: params["fbc"],
        },
        timestamp: new Date().toISOString(),
      };

      log("Event data prepared", "info", eventData);

      // Check if consent is given
      if (utils.getConsentStatus(config.storageKeys.consentGiven)) {
        log("Consent given, sending event data", "info");
        // Send event data to sGTM
        utils
          .sendToSgtm(eventData, config.endpoint, config.storageKeys.eventSent)
          .catch((error) => {
            log("Error sending event data", "error", error);
          });
      } else {
        log("Consent not given, storing event data for later", "info");
        // Store the event data to send later when consent is given
        utils.storeEventLocalStorage(eventData);
        window.VerifiedTracking._pendingEvent = eventData;
      }
    }
  }

  function setupUnloadTracking() {
    if (!config.enableUnloadTracking) return;

    const unloadHandler = () => {
      const totalTimeSpent = Math.floor((Date.now() - pageStartTime) / 1000);

      log("Page unloading, sending final time tracking", "info", {
        totalTimeSpent,
        timeElapsed,
      });

      // Only send if user has given consent
      if (utils.getConsentStatus(config.storageKeys.consentGiven)) {
        const unloadEventData = {
          event: config.unloadEventName,
          url: window.location.href,
          params: params,
          engagement: {
            totalTimeOnPage: totalTimeSpent,
            mainEventTriggered: eventTriggered,
          },
          timestamp: new Date().toISOString(),
          eventType: "page_unload",
        };

        // Use existing sendToSgtm method with storeOnFailure = false for unload
        utils
          .sendToSgtm(
            unloadEventData,
            config.endpoint,
            config.storageKeys.eventSent + "_unload",
            false
          )
          .catch((error) => {
            log("Error sending unload event", "error", error);
          });
      }
    };

    // Add multiple event listeners for better coverage
    const events = ["pagehide"];

    events.forEach((eventType) => {
      const handler =
        eventType === "visibilitychange"
          ? () => {
              if (document.visibilityState === "hidden") {
                unloadHandler();
              }
            }
          : unloadHandler;

      window.addEventListener(eventType, handler);
      unloadHandlers.push({ eventType, handler });
    });

    log("Unload tracking set up", "info");
  }

  function removeUnloadHandlers() {
    unloadHandlers.forEach(({ eventType, handler }) => {
      window.removeEventListener(eventType, handler);
    });
    unloadHandlers = [];
  }

  // Start tracking immediately
  startTracking();

  return controller;
}

/**
 * Initialize tracking
 * @param {Object} config - Configuration object
 * @returns {Object|null} - Tracking controller or null if not tracking
 */
export function initTracking(config) {
  log("Initializing tracking", "info", config);

  // Exit early if we've already sent this event in this session
  if (sessionStorage.getItem(config.storageKeys.eventSent) === "true") {
    log("Event already sent in this session, exiting", "info");
    return null;
  }

  // Get parameters with fallback to referrer
  const params = utils.getParamsWithFallback(config.storageKeys.params);
  const newParamsExist = params && Object.keys(params).length > 0;
  const storedParams = utils.getStoredParams(config.storageKeys.params);
  const oldParamsExist = storedParams && Object.keys(storedParams).length > 0;

  // Store parameters in sessionStorage for later use if we got any and there are no previously saved params
  if (newParamsExist && !oldParamsExist) {
    utils.storeParams(params, config.storageKeys.params);
  }

  log("Using parameters for tracking decision", "info", {
    source:
      utils.getParamsWithFallback() &&
      Object.keys(utils.getParamsWithFallback()).length > 0
        ? "URL"
        : utils.getStoredParams(config.storageKeys.params)
          ? "Stored"
          : "Referrer",
    params,
  });

  // Check if we should track this session
  if (
    !utils.shouldTrackSession(
      params,
      config.requiredUtmParam,
      config.requiredUtmValue
    )
  ) {
    log("Session does not meet tracking criteria, exiting", "info");
    return null;
  }

  log("Starting engagement tracking", "info");
  // Start tracking engagement
  if (window.VerifiedTracking && window.VerifiedTracking._controller) {
    window.VerifiedTracking._controller.destroy();
    window.VerifiedTracking._controller = null;
  }
  window.VerifiedTracking._controller = trackEngagement(params, config);
  return window.VerifiedTracking._controller;
}

/**
 * Process pending events after consent is given
 * @param {Object} config - Configuration object
 */
export async function processPendingEvents(config) {
  const pendingEvents =
    window.VerifiedTracking._pendingEvent || utils.getEventLocalStorage();
  if (Object.entries(pendingEvents).length) {
    log("Processing pending events after consent", "info", pendingEvents);

    try {
      await utils.sendToSgtm(
        pendingEvents,
        config.endpoint,
        config.storageKeys.eventSent
      );

      log("Successfully sent pending event", "info");
      window.VerifiedTracking._pendingEvent = null;
      utils.clearEventLocalStorage();
    } catch (error) {
      log("Error sending pending event data", "error", error);
    }
  } else {
    log("No pending events to process", "info");
  }
}
